import { useState } from "react";
import axios from "axios";
import PageHeader from "@/components/PageHeader";
import { toast } from "sonner";
import { Send, Loader2, CheckCircle2 } from "lucide-react";
import { Link } from "react-router-dom";
import { WHATSAPP_URL } from "@/lib/brand";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const initial = {
  name: "",
  email: "",
  phone: "",
  session_type: "individual",
  specialist: "indistinto",
  reason: "",
  prior_therapy: "no",
  preferred_time: "",
  urgency: "normal",
};

const Cuestionario = () => {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const setField = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    const required = ["name", "email", "phone", "reason"];
    for (const r of required) {
      if (!form[r] || form[r].trim().length < 2) {
        toast.error("Completa todos los campos marcados.");
        return;
      }
    }
    try {
      setSubmitting(true);
      const { data } = await axios.post(`${API}/questionnaire`, form);
      setDone(true);
      if (data?.email_sent) {
        toast.success("Cuestionario recibido, te contactaremos pronto.");
      } else {
        toast.success("Cuestionario recibido, te contactaremos pronto.");
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || "Ocurrió un error. Intenta de nuevo.";
      toast.error(typeof msg === "string" ? msg : "Error al enviar el cuestionario.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div data-testid="cuestionario-done" className="px-6 md:px-14 py-24 max-w-3xl">
        <div className="card-soft p-10" style={{ background: "#fbf6ec" }}>
          <CheckCircle2 size={44} style={{ color: "var(--p-burgundy)" }} strokeWidth={1.2} />
          <h1
            className="mt-5"
            style={{
              fontFamily: "Cormorant Garamond, serif",
              fontSize: "clamp(1.8rem, 3vw, 2.4rem)",
              fontWeight: 500,
              color: "var(--p-black)",
            }}
          >
            Gracias por compartirnos tu historia.
          </h1>
          <p
            className="mt-3"
            style={{
              fontFamily: "Cormorant Garamond, serif",
              color: "var(--p-brown)",
              fontSize: "1.1rem",
              lineHeight: 1.6,
            }}
          >
            Hemos recibido tu cuestionario. Uno de nuestros psicólogos te contactará
            en un plazo aproximado de 24 horas para confirmar disponibilidad, tarifa
            y coordinar tu primera sesión.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <a href={WHATSAPP_URL} target="_blank" rel="noreferrer" className="btn-primary" data-testid="done-whatsapp">
              Escribir por WhatsApp
            </a>
            <Link to="/" className="btn-ghost">Volver al inicio</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="cuestionario-page">
      <PageHeader
        eyebrow="Cuestionario previo"
        title="Ayúdanos a preparar tu primera sesión."
        subtitle="Un breve cuestionario (2 minutos) que nos permite entender tu situación y asignarte el proceso más adecuado. Toda la información es confidencial y se envía cifrada."
      />

      <form
        onSubmit={onSubmit}
        data-testid="cuestionario-form"
        className="px-6 md:px-14 pb-24 max-w-4xl"
      >
        <div className="card-soft p-8 md:p-10 space-y-8" style={{ background: "#fbf6ec" }}>
          <Section number="01" title="Datos de contacto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <TextField testid="q-name" label="Nombre completo *" value={form.name} onChange={setField("name")} />
              <TextField testid="q-email" label="Correo electrónico *" type="email" value={form.email} onChange={setField("email")} />
            </div>
            <div className="mt-4">
              <TextField testid="q-phone" label="WhatsApp / Teléfono *" type="tel" value={form.phone} onChange={setField("phone")} placeholder="52 55 1234 5678" />
            </div>
          </Section>

          <Section number="02" title="¿Qué tipo de sesión estás buscando?">
            <RadioGroup
              testid="q-session-type"
              value={form.session_type}
              onChange={(v) => setForm({ ...form, session_type: v })}
              options={[
                { value: "individual", label: "Individual", note: "Trabajo personal 1 a 1" },
                { value: "pareja", label: "Pareja", note: "Dinámicas de la relación" },
                { value: "familia", label: "Familia", note: "Padres / hijos / conjunto" },
              ]}
            />
          </Section>

          <Section number="03" title="¿Con qué psicólogo prefieres trabajar?">
            <RadioGroup
              testid="q-specialist"
              value={form.specialist}
              onChange={(v) => setForm({ ...form, specialist: v })}
              options={[
                { value: "katia", label: "Mtra. Katia Marcin", note: "Fundadora · TCC / DBT / Sistémica" },
                { value: "gerardo", label: "Mtro. Gerardo Abadía", note: "Cofundador · Humanista / Gestalt / TCC" },
                { value: "indistinto", label: "Sin preferencia", note: "Nos guiamos por disponibilidad" },
              ]}
            />
          </Section>

          <Section number="04" title="¿Qué te está trayendo a consulta?">
            <TextArea
              testid="q-reason"
              label="Cuéntanos brevemente. No necesitamos detalles clínicos, sólo lo que quieras compartir. *"
              value={form.reason}
              onChange={setField("reason")}
            />
          </Section>

          <Section number="05" title="Algunas preguntas breves">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <RadioGroup
                testid="q-prior"
                label="¿Has tomado terapia psicológica antes?"
                value={form.prior_therapy}
                onChange={(v) => setForm({ ...form, prior_therapy: v })}
                options={[
                  { value: "si", label: "Sí" },
                  { value: "no", label: "No" },
                ]}
                compact
              />
              <RadioGroup
                testid="q-urgency"
                label="¿Con qué urgencia buscas comenzar?"
                value={form.urgency}
                onChange={(v) => setForm({ ...form, urgency: v })}
                options={[
                  { value: "normal", label: "Sin prisa" },
                  { value: "pronto", label: "Cuanto antes" },
                  { value: "urgente", label: "Es urgente" },
                ]}
                compact
              />
            </div>
            <div className="mt-6">
              <TextField
                testid="q-time"
                label="Horario preferido (opcional)"
                value={form.preferred_time}
                onChange={setField("preferred_time")}
                placeholder="Ej. Lunes y miércoles después de las 6 pm"
              />
            </div>
          </Section>

          <div className="pt-2">
            <p
              className="text-[13px]"
              style={{ color: "var(--p-brown)", fontFamily: "Cormorant Garamond, serif" }}
            >
              Al enviar, confirmas que has leído y aceptas nuestro{" "}
              <Link to="/aviso-de-privacidad" style={{ color: "var(--p-burgundy)", textDecoration: "underline" }}>
                Aviso de Privacidad
              </Link>{" "}
              y nuestra{" "}
              <Link to="/politica-de-confidencialidad" style={{ color: "var(--p-burgundy)", textDecoration: "underline" }}>
                Política de Confidencialidad
              </Link>
              .
            </p>
            <button
              type="submit"
              disabled={submitting}
              data-testid="q-submit"
              className="btn-primary mt-6"
              style={submitting ? { opacity: 0.7, cursor: "not-allowed" } : undefined}
            >
              {submitting ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  Enviando…
                </>
              ) : (
                <>
                  <Send size={16} />
                  Enviar cuestionario
                </>
              )}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

const Section = ({ number, title, children }) => (
  <div>
    <div className="flex items-baseline gap-3">
      <span
        style={{
          fontFamily: "Cinzel, serif",
          fontSize: "12px",
          color: "var(--p-gold-soft)",
          letterSpacing: "0.24em",
        }}
      >
        {number}
      </span>
      <h3
        style={{
          fontFamily: "Cormorant Garamond, serif",
          fontSize: "1.35rem",
          fontWeight: 600,
          color: "var(--p-black)",
        }}
      >
        {title}
      </h3>
    </div>
    <div className="mt-5">{children}</div>
  </div>
);

const TextField = ({ label, testid, ...props }) => (
  <label data-testid={testid} className="block">
    <span className="eyebrow block" style={{ color: "var(--p-brown)" }}>{label}</span>
    <input
      {...props}
      className="w-full bg-transparent outline-none py-2 border-b mt-2"
      style={{
        borderColor: "rgba(74,21,33,0.35)",
        color: "var(--p-black)",
        fontFamily: "Cormorant Garamond, serif",
        fontSize: "1.05rem",
      }}
    />
  </label>
);

const TextArea = ({ label, testid, ...props }) => (
  <label data-testid={testid} className="block">
    <span className="eyebrow block" style={{ color: "var(--p-brown)" }}>{label}</span>
    <textarea
      rows={5}
      {...props}
      className="w-full bg-transparent outline-none py-2 border-b mt-2 resize-none"
      style={{
        borderColor: "rgba(74,21,33,0.35)",
        color: "var(--p-black)",
        fontFamily: "Cormorant Garamond, serif",
        fontSize: "1.05rem",
      }}
    />
  </label>
);

const RadioGroup = ({ testid, label, value, onChange, options, compact }) => (
  <div data-testid={testid}>
    {label && <span className="eyebrow block mb-3" style={{ color: "var(--p-brown)" }}>{label}</span>}
    <div className={`grid gap-3 ${compact ? "grid-cols-1 sm:grid-cols-3" : "grid-cols-1 md:grid-cols-3"}`}>
      {options.map((o) => {
        const active = value === o.value;
        return (
          <button
            key={o.value}
            type="button"
            data-testid={`${testid}-${o.value}`}
            onClick={() => onChange(o.value)}
            className="text-left p-4 rounded-sm transition-all"
            style={{
              border: active
                ? "1px solid var(--p-burgundy)"
                : "1px solid rgba(138,116,101,0.28)",
              background: active ? "rgba(74,21,33,0.05)" : "#ffffff",
              boxShadow: active ? "0 6px 20px -14px rgba(74,21,33,0.4)" : "none",
            }}
          >
            <div className="flex items-center gap-2">
              <span
                className="w-4 h-4 rounded-full border flex items-center justify-center shrink-0"
                style={{ borderColor: "var(--p-burgundy)" }}
              >
                {active && (
                  <span
                    className="w-2 h-2 rounded-full"
                    style={{ background: "var(--p-burgundy)" }}
                  />
                )}
              </span>
              <span
                style={{
                  fontFamily: "Cormorant Garamond, serif",
                  fontSize: "1.05rem",
                  color: "var(--p-black)",
                  fontWeight: 600,
                }}
              >
                {o.label}
              </span>
            </div>
            {o.note && (
              <div
                className="mt-1"
                style={{
                  fontFamily: "Cormorant Garamond, serif",
                  fontSize: "0.9rem",
                  color: "var(--p-brown)",
                  paddingLeft: 22,
                }}
              >
                {o.note}
              </div>
            )}
          </button>
        );
      })}
    </div>
  </div>
);

export default Cuestionario;
